import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
    FaTruck, FaEdit, FaFilePdf, FaHistory, FaTrash, FaPlusCircle, FaSearch, FaExclamationCircle, FaCheckCircle, FaEye, FaBan, FaLock
} from 'react-icons/fa';
import { 
    fetchLivraisons, createLivraison, updateLivraison, deleteLivraison, fetchClients, fetchPrestations, exportLivraisonPDF 
} from '../api'; 

const BonDeLivraisonPage = ({ user }) => {
    const location = useLocation();
    
    const [livraisons, setLivraisons] = useState([]);
    const [clients, setClients] = useState([]);
    const [prestations, setPrestations] = useState([]);
    const [lastLivraisonId, setLastLivraisonId] = useState(null); 
    
    const [clientSearch, setClientSearch] = useState('');
    const [currentLines, setCurrentLines] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(''); 
    const [prestationSearchTerm, setPrestationSearchTerm] = useState(''); 
    const [showSuggestions, setShowSuggestions] = useState(false);        
    const [selectedPrestation, setSelectedPrestation] = useState(null);   

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); 
    
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);
    const [viewLivraison, setViewLivraison] = useState(null);

    // 🔒 SÉCURITÉ
    const userEmail = user?.email?.toLowerCase() || user?.username?.toLowerCase() || '';
    const isKhKoffi = userEmail === 'kh.koffi@arsn.ci' || userEmail === 'kh.koffi@gmail.com';
    const isSuperAdmin = user?.role === 'superadmin';
    const canCancel = isKhKoffi || isSuperAdmin; 

    const loadData = async () => {
        setLoading(true);
        try {
            const [resLiv, resCli, resPrest] = await Promise.all([
                fetchLivraisons(), fetchClients(), fetchPrestations()
            ]);
            const docsList = resLiv.data || [];
            setLivraisons(docsList);
            
            if (docsList.length > 0) {
                const latest = docsList.reduce((prev, current) => {
                    return (new Date(prev.createdAt || prev.date) > new Date(current.createdAt || current.date)) ? prev : current;
                });
                setLastLivraisonId(latest._id);
            } else {
                setLastLivraisonId(null);
            }

            setClients(resCli.data);
            setPrestations(resPrest.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur de connexion au serveur.' });
        } finally { setLoading(false); }
    };

    useEffect(() => { 
        loadData(); 
    }, [location]);

    const filteredPrestations = prestationSearchTerm.length > 0
        ? prestations.filter(p => p.name.toLowerCase().includes(prestationSearchTerm.toLowerCase()) || p.code.toLowerCase().includes(prestationSearchTerm.toLowerCase())).slice(0, 50) 
        : [];

    const filteredLivraisons = livraisons.filter(l => 
        (l.number.toLowerCase().includes(search.toLowerCase()) || l.clientName.toLowerCase().includes(search.toLowerCase())) && 
        (statusFilter === 'all' || l.status === statusFilter)
    );

    const handleSelectSuggestion = (p) => {
        setSelectedPrestation(p);          
        setPrestationSearchTerm(p.name);    
        setShowSuggestions(false);          
    };

    const handleAddPrestation = () => {
        if (!selectedPrestation) return;
        // On n'intègre plus les prix ni les détails superflus
        const newLine = { 
            prestationId: selectedPrestation._id, 
            code: selectedPrestation.code, 
            name: selectedPrestation.name, 
            quantity: 1, 
            price: 0, 
            total: 0 
        };
        setCurrentLines([...currentLines, newLine]);
        setSelectedPrestation(null); setPrestationSearchTerm(''); setMessage(null); 
    };

    const updateLine = (idx, field, val) => {
        const newLines = [...currentLines];
        newLines[idx][field] = val;
        setCurrentLines(newLines);
    };

    const removeLine = (idx) => setCurrentLines(currentLines.filter((_, i) => i !== idx));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientSearch || currentLines.length === 0) return;

        const payload = { 
            clientName: clientSearch, 
            lines: currentLines, 
            date: deliveryDate, 
            dueDate: dueDate,
            remise: 0 // Forcé à 0 car pas de finance
        };

        try {
            if (editingId) { 
                await updateLivraison(editingId, payload); 
                setMessage({ type: 'success', text: '✅ BL mis à jour avec succès' }); 
            } else { 
                await createLivraison(payload); 
                setMessage({ type: 'success', text: '✅ Bon de livraison émis avec succès !' }); 
            }
            
            await loadData(); 
            
            setTimeout(() => {
                setEditingId(null); 
                setClientSearch(''); 
                setCurrentLines([]); 
                setDueDate(''); 
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 50);

        } catch (err) { 
            const errorText = err.response?.data?.message || 'Erreur lors de l\'enregistrement.';
            setMessage({ type: 'error', text: errorText }); 
        }
    };

    const handleDownloadPDF = async (id, number) => {
        setDownloadingPdfId(id);
        try {
            const res = await exportLivraisonPDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); 
            link.href = url;
            link.setAttribute('download', `BL_${number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link); 
            link.click();
            link.remove(); 
            window.URL.revokeObjectURL(url); 
        } catch (err) { 
            setMessage({ type: 'error', text: "Erreur lors du téléchargement du PDF." }); 
        } finally {
            setDownloadingPdfId(null);
        }
    };

    const handleCancel = async (l) => {
        if (!window.confirm(`⚠️ Voulez-vous vraiment ANNULER le BL ${l.number} ?`)) return;
        try { 
            await updateLivraison(l._id, { status: 'annulée' }); 
            loadData(); 
            setMessage({type:'success', text:'✅ Bon de livraison annulé avec succès.'}); 
        } catch (e) { 
            setMessage({type:'error', text: e.response?.data?.message || 'Erreur lors de l\'annulation.'}); 
        }
    };

    const handleDelete = async (l) => {
        if (l._id !== lastLivraisonId) {
            setMessage({ type: 'error', text: '⛔ Action refusée : Vous ne pouvez supprimer que le tout dernier BL émis.' });
            return;
        }
        if (user?.role !== 'admin') return;
        if (!window.confirm("⚠️ ATTENTION : Supprimer ce BL créera un trou dans la numérotation. Supprimer quand même ?")) return;
        try { 
            await deleteLivraison(l._id); 
            loadData(); 
            setMessage({type:'success', text:'✅ Supprimé par l\'administrateur.'}); 
        } catch (e) { 
            setMessage({type:'error', text: e.response?.data?.message || 'Erreur lors de la suppression.'}); 
        }
    };

    const startEdit = (l) => {
        if (l._id !== lastLivraisonId) {
            setMessage({ type: 'error', text: '⛔ Action refusée : Les anciens BL sont verrouillés.' });
            return;
        }
        if (l.status === 'annulée') {
            setMessage({ type: 'error', text: `⛔ Impossible de modifier un document annulé.` });
            return;
        }
        setEditingId(l._id);
        setClientSearch(l.clientName);
        setCurrentLines(l.lines);
        if(l.date) setDeliveryDate(new Date(l.date).toISOString().split('T')[0]);
        if(l.dueDate) setDueDate(new Date(l.dueDate).toISOString().split('T')[0]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMessage({ type: 'warning', text: "✏️ MODE ÉDITION BL" });
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4" translate="no">
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaTruck className="me-2 text-primary"/> BONS DE LIVRAISON
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Gestion et émission des bons de livraison (BL)</p>
                </div>
            </div>

            <div style={{ minHeight: '50px', marginBottom: '1rem' }}>
                <div 
                    className={`alert py-2 mb-0 shadow-sm border-0 ${message?.type === 'error' ? 'alert-danger' : (message?.type === 'warning' ? 'alert-warning' : 'alert-success')}`}
                    style={{ display: message ? 'block' : 'none' }}
                >
                    {message?.text}
                </div>
            </div>

            {/* FORMULAIRE D'ÉMISSION */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-primary" style={{ letterSpacing: '1px' }}>
                    <span style={{ display: editingId ? 'inline' : 'none' }}>MODIFIER LE DERNIER BL</span>
                    <span style={{ display: !editingId ? 'inline' : 'none' }}>ÉMETTRE UN NOUVEAU BL</span>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-6">
                                <label className="fw-bold small mb-1 text-muted">CLIENT *</label>
                                <input className="form-control form-control-sm" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} list="clientsListBl" placeholder="Rechercher ou saisir un client..." />
                                <datalist id="clientsListBl">{clients.map(c => <option key={c._id} value={c.name} />)}</datalist>
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE ÉMISSION *</label>
                                <input type="date" className="form-control form-control-sm" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE PRÉVUE (Optionnel)</label>
                                <input type="date" className="form-control form-control-sm" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </div>
                        </div>

                        {/* RECHERCHE PRESTATION */}
                        <div className="p-3 mb-4 rounded bg-light border">
                            <label className="fw-bold small mb-2 text-primary">RECHERCHER UN ARTICLE / PRESTATION</label>
                            <div className="position-relative">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white text-muted border-end-0"><FaSearch/></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Nom ou code..." value={prestationSearchTerm}
                                        onChange={(e) => { setPrestationSearchTerm(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    <button type="button" className={`btn fw-bold px-3 text-white ${selectedPrestation ? 'btn-primary' : 'btn-secondary'}`} onClick={handleAddPrestation} disabled={!selectedPrestation}>
                                        <FaPlusCircle className="me-1" /> AJOUTER
                                    </button>
                                </div>
                                {showSuggestions && prestationSearchTerm && (
                                    <div className="position-absolute w-100 shadow bg-white border rounded mt-1" style={{zIndex: 1000, maxHeight: '200px', overflowY: 'auto'}}>
                                        {filteredPrestations.map(p => (
                                            <div key={p._id} className="p-2 border-bottom cursor-pointer text-dark text-decoration-none bg-light-hover" onMouseDown={() => handleSelectSuggestion(p)} style={{cursor: 'pointer'}}>
                                                <div className="fw-bold">{p.name}</div>
                                                <div className="small text-muted">{p.code}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TABLEAU DES LIGNES (SANS PRIX) */}
                        <div className="table-responsive mb-4 border rounded">
                            <table className="table table-sm mb-0 align-middle table-hover text-nowrap">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3 py-2 text-muted" style={{width: '70%'}}>Désignation de l'article / Prestation</th>
                                        <th className="text-center py-2 text-muted" style={{width: '20%'}}>Quantité livrée</th>
                                        <th className="text-center py-2 text-muted" style={{width: '10%'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ display: currentLines.length === 0 ? 'table-row' : 'none' }}>
                                        <td colSpan="3" className="text-center py-4 text-muted"><em>Aucune ligne ajoutée.</em></td>
                                    </tr>
                                    {currentLines.map((line, idx) => (
                                        <tr key={`line-${idx}-${line.prestationId || line.code}`}>
                                            <td className="ps-3 fw-bold text-wrap text-dark">{line.name}</td>
                                            <td><input type="number" className="form-control form-control-sm text-center mx-auto" style={{maxWidth: '100px'}} value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} min="1" /></td>
                                            <td className="text-center"><button type="button" className="btn btn-sm btn-outline-danger p-1 border-0" onClick={() => removeLine(idx)}><FaTrash size={16}/></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center pt-3 mt-3 border-top gap-3">
                            <div className="text-muted small fst-italic">* Champs obligatoires</div>
                            <div className="d-flex align-items-center gap-3 mt-3 mt-md-0">
                                <div style={{ minWidth: '200px', textAlign: 'right' }}>
                                    <span 
                                        className="small fw-bold text-warning" 
                                        style={{ visibility: (!clientSearch || currentLines.length === 0) ? 'visible' : 'hidden' }}
                                    >
                                        <FaExclamationCircle className="me-1"/> Informations incomplètes
                                    </span>
                                </div>
                                <button 
                                    className={`btn px-4 py-2 fw-bold text-white d-flex align-items-center justify-content-center ${currentLines.length === 0 || !clientSearch ? 'btn-secondary' : 'btn-primary'}`} 
                                    type="submit" 
                                    disabled={currentLines.length === 0 || !clientSearch}
                                >
                                    <span style={{ display: editingId ? 'inline-flex' : 'none', alignItems: 'center' }}>
                                        <FaEdit className="me-2"/> METTRE À JOUR
                                    </span>
                                    <span style={{ display: !editingId ? 'inline-flex' : 'none', alignItems: 'center' }}>
                                        <FaCheckCircle className="me-2"/> ÉMETTRE LE BL
                                    </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* HISTORIQUE */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark" style={{ letterSpacing: '1px' }}><FaHistory className="me-2 text-primary"/>HISTORIQUE DES BL</span>
                    <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
                        <select className="form-select form-select-sm w-100 w-sm-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">Tous statuts</option>
                            <option value="émise">Émise</option>
                            <option value="annulée">Annulée</option>
                        </select>
                        <div className="input-group input-group-sm w-100 w-sm-auto">
                            <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                            <input className="form-control border-start-0" placeholder="Rechercher n° ou client..." value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">N° BL</th>
                                <th className="py-3 border-bottom-0">Client</th>
                                <th className="py-3 border-bottom-0">Date d'émission</th>
                                <th className="py-3 border-bottom-0 text-center">Statut</th>
                                <th className="pe-3 py-3 text-end border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ display: filteredLivraisons.length === 0 ? 'table-row' : 'none' }}>
                                <td colSpan="5" className="text-center py-4 text-muted">Aucun BL trouvé.</td>
                            </tr>
                            {filteredLivraisons.map(l => {
                                const isLocked = l._id !== lastLivraisonId;
                                return (
                                    <tr key={l._id}>
                                        <td className="ps-3 fw-bold text-primary">
                                            {isLocked && l.status !== 'annulée' && <FaLock className="text-muted me-2" size={10} title="Document verrouillé" />}
                                            {l.number}
                                        </td>
                                        <td className="text-wrap text-dark fw-bold" style={{minWidth: '150px'}}>{l.clientName}</td>
                                        <td className="text-muted small">{new Date(l.date).toLocaleDateString()}</td>
                                        <td className="text-center">
                                            <span className={`badge rounded-1 px-2 py-1 border ${
                                                l.status === 'annulée' ? 'bg-danger text-danger bg-opacity-10 border-danger' :
                                                'bg-success text-success bg-opacity-10 border-success'}`}>
                                                {l.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-end pe-3">
                                            <button className="btn btn-sm btn-outline-info rounded-1 me-1 py-1 px-2" onClick={() => setViewLivraison(l)} title="Voir les détails"><FaEye size={14}/></button>

                                            {!isLocked && l.status !== 'annulée' && (
                                                <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(l)} title="Modifier le dernier BL"><FaEdit size={14}/></button>
                                            )}
                                            
                                            <button 
                                                className="btn btn-sm btn-outline-danger rounded-1 me-1 py-1 px-2"
                                                onClick={() => handleDownloadPDF(l._id, l.number)} 
                                                title="Télécharger PDF"
                                                disabled={downloadingPdfId === l._id}
                                            >
                                                {downloadingPdfId === l._id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <FaFilePdf size={14}/>}
                                            </button>
                                            
                                            {(canCancel && l.status !== 'annulée') && (
                                                <button className="btn btn-sm btn-outline-warning rounded-1 me-1 py-1 px-2" onClick={() => handleCancel(l)} title="Annuler le BL"><FaBan size={14}/></button>
                                            )}

                                            {(user?.role === 'admin' && !isLocked) && (
                                                <button className="btn btn-sm btn-outline-secondary rounded-1 py-1 px-2" onClick={() => handleDelete(l)} title="Supprimer (Admin)"><FaTrash size={14}/></button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL POUR VISUALISER LE BL (SANS PRIX) */}
            {viewLivraison && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-light py-3 border-bottom">
                                <h5 className="modal-title fs-6 fw-bold text-uppercase text-primary" style={{ letterSpacing: '1px' }}>Détails BL : {viewLivraison.number}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewLivraison(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row mb-4">
                                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                                        <h6 className="fw-bold small mb-1 text-muted">CLIENT À LIVRER</h6>
                                        <div className="fs-6 fw-bold text-dark">{viewLivraison.clientName}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">DATE ÉMISSION</h6>
                                        <div className="fw-bold text-dark">{new Date(viewLivraison.date).toLocaleDateString('fr-FR')}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">STATUT</h6>
                                        <span className={`badge rounded-1 px-3 py-2 border ${
                                            viewLivraison.status === 'annulée' ? 'bg-danger text-danger bg-opacity-10 border-danger' :
                                            'bg-success text-success bg-opacity-10 border-success'}`}>
                                            {viewLivraison.status.toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div className="table-responsive border rounded">
                                    <table className="table table-sm mb-0 table-hover align-middle">
                                        <thead className="bg-light small text-uppercase text-muted">
                                            <tr>
                                                <th className="ps-3 py-2 border-bottom-0" style={{width: '80%'}}>Désignation de l'article / Prestation</th>
                                                <th className="text-center py-2 border-bottom-0" style={{width: '20%'}}>Quantité</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewLivraison.lines?.map((l, i) => (
                                                <tr key={i}>
                                                    <td className="ps-3 py-2 text-wrap">
                                                        <div className="fw-bold text-dark">{l.name}</div>
                                                    </td>
                                                    <td className="text-center py-2 text-dark">{l.quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer py-3 border-top bg-light">
                                <button className="btn btn-sm btn-secondary px-4 fw-bold" onClick={() => setViewLivraison(null)}>FERMER</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BonDeLivraisonPage;